import { TableContainer, Table, TableHead, TableRow, TableCell, TableBody, TableFooter } from '@mui/material';
import { summaryTableMessages, percentageFormatter, amountFormatter, PirPositionTypesEnum, useAppSelector, PirBoxState } from 'azshared';
import { PirBoxProductDetail } from 'azshared/build/main/lib/model/pirbox/pirbox-product-summary';
import React from 'react';
import { useIntl } from 'react-intl';

export interface PirBoxSummaryTablePositionProps {
  position: PirBoxProductDetail;
  expirationDate: Date;
}

export const PirBoxSummaryTablePosition = (props: PirBoxSummaryTablePositionProps) => {
  const { formatMessage } = useIntl();
  const { typology, weight, positions: products } = props.position;
  const { positions: positionsWithValidCommitments } = useAppSelector<PirBoxState>((state) => state.features.pirbox);
  const commitmentsValues = Array.from(positionsWithValidCommitments?.values() ?? []).flat();
  const isQualified = typology === PirPositionTypesEnum.Qualificata;
  const typologyLabel = isQualified ? formatMessage(summaryTableMessages.qualifiedTypology) : formatMessage(summaryTableMessages.notQualifiedTypology);
  let totalDivested = 0;
  let totalInvested = 0;
  let totalCommitment = 0;

  for (const product of products) {
    totalDivested += product.totalDivested?.value;
    totalInvested += product.totalInvested?.value;
    totalCommitment += commitmentsValues.find((commitment) => commitment.codTitle === product.titleCode)?.commitment?.value ?? 0;
  }

  const totalAmountDivested = {
    value: totalDivested || 0,
    currency: products[0]?.totalDivested?.currency
  };

  const totalAmountInvested = {
    value: totalInvested || 0,
    currency: products[0]?.totalInvested?.currency
  };

  const totalCommitmentAmount = {
    value: totalCommitment || 0,
    currency: products[0]?.committment?.currency
  };
  return (
    <div>
      <div className='typology-section'>
        <div>
          <p className='cell-weight'>{formatMessage(summaryTableMessages.typology)}</p>
          <p>{typologyLabel}</p>
        </div>
        <div>
          <p className='cell-weight'>
            {formatMessage(summaryTableMessages.composition)}
            <sup>3</sup>
          </p>
          <p>{weight ? percentageFormatter(weight, { isPercentage: true }) : '-'}</p>
        </div>
      </div>
      <div>
        <TableContainer className='document-detail-operations-table'>
          <Table aria-label='collapsible table'>
            <TableHead>
              <TableRow className='document-detail-operations-table-row'>
                <TableCell className='document-detail-operations-table-header'>
                  <p>{formatMessage(summaryTableMessages.productTitle)}</p>
                </TableCell>
                <TableCell className='document-detail-operations-table-header' align='left'>
                  <p>
                    {formatMessage(summaryTableMessages.invested)}
                    <sup>5-6</sup>
                  </p>
                </TableCell>
                <TableCell className='document-detail-operations-table-header' align='left'>
                  <p>
                    {formatMessage(summaryTableMessages.divested)}
                    <sup>5</sup>
                  </p>
                </TableCell>
                <TableCell className='document-detail-operations-table-header' align='left'>
                  <p>{formatMessage(summaryTableMessages.commitmentBooked)}</p>
                </TableCell>
                <TableCell className='document-detail-operations-table-header' align='left'>
                  <p>
                    {formatMessage(summaryTableMessages.concetration)}
                    <sup>3</sup>
                  </p>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {products?.map((product) => {
                return (
                  <TableRow key={product?.titleCode}>
                    <TableCell
                      className='document-detail-operations-table-row-cell product-title'
                      component='th'
                      scope='row'
                      sx={{
                        borderBottomLeftRadius: 8,
                        borderTopLeftRadius: 8
                      }}>
                      {product?.description}
                    </TableCell>
                    <TableCell className='document-detail-operations-table-row-cell' align='left'>
                      {amountFormatter(product?.totalInvested)}
                    </TableCell>
                    <TableCell className='document-detail-operations-table-row-cell' align='left'>
                      {amountFormatter(product?.totalDivested)}
                    </TableCell>
                    <TableCell className='document-detail-operations-table-row-cell' align='left'>
                      {amountFormatter(
                        commitmentsValues?.find((commitment) => commitment?.codTitle === product?.titleCode)?.commitment ?? {
                          value: 0,
                          currency: product?.committment?.currency
                        }
                      )}
                    </TableCell>
                    <TableCell className='document-detail-operations-table-row-cell' align='left'>
                      {product?.concentration ? percentageFormatter(product?.concentration, { isPercentage: true }) : '-'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
            <TableFooter className='document-detail-operations-table-row'>
              <TableRow>
                <TableCell className='summary-footer'>
                  <p className='cell-weight'>{formatMessage(summaryTableMessages.total)}</p>
                </TableCell>
                <TableCell>{amountFormatter(totalAmountInvested)}</TableCell>
                <TableCell>{amountFormatter(totalAmountDivested)}</TableCell>
                <TableCell>{amountFormatter(totalCommitmentAmount)}</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </div>
    </div>
  );
};
